/**
 * Раздел «Техника» (поток 9, разделы 6.32 и 6.33 ТЗ): парк с документами и сроками, наряды и путевые листы с
 * телефона, табель и загрузка, топливо против нормы, стоимость машино-часа, выработка по работам.
 */
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useCrumbs } from '../../layout/crumbs';
import { EmptyState } from '../../ui/feedback';
import { Page, PageHeader } from '../../ui/layout';
import { ProjectFilter, Tabs } from '../psd/shared';
import { FleetPark } from './FleetPark';
import { CostPanel, FuelPanel, OutputPanel, TimesheetPanel } from './FleetReports';
import { ServicePanel } from './FleetService';
import { FleetWaybills } from './FleetWaybills';

type Tab = 'park' | 'service' | 'waybills' | 'timesheet' | 'fuel' | 'cost' | 'output';
type Search = { tab?: Tab; project?: string; machine?: string };

const TABS: Array<{ value: Tab; label: string }> = [
  { value: 'park', label: 'Парк' },
  { value: 'service', label: 'ТО' },
  { value: 'waybills', label: 'Наряды и путевые листы' },
  { value: 'timesheet', label: 'Табель и загрузка' },
  { value: 'fuel', label: 'Топливо' },
  { value: 'cost', label: 'Стоимость часа' },
  { value: 'output', label: 'Выработка' },
];

export function FleetPage() {
  const search = useSearch({ strict: false }) as Search;
  const navigate = useNavigate();
  const tab: Tab = search.tab ?? 'park';
  const setSearch = (patch: Partial<Search>) =>
    navigate({ to: '/fleet', search: { ...search, ...patch } as never, replace: true });
  useCrumbs(tab === 'park' ? null : [{ label: TABS.find((t) => t.value === tab)!.label }]);
  const project = search.project;
  return (
    <Page>
      <PageHeader
        title="Техника"
        subtitle="Парк, документы со сроками, наряды и путевые листы с телефона, табель, топливо против нормы, стоимость машино-часа и выработка."
      />
      <Tabs value={tab} onChange={(v) => setSearch({ tab: v === 'park' ? undefined : v })} items={TABS} />
      <div className="mb-4">
        <ProjectFilter
          value={project}
          onChange={(v) => setSearch({ project: v })}
          perms={['resources.view', 'site.view', 'site.report']}
        />
      </div>
      {tab === 'park' ? <FleetPark projectId={project} open={search.machine} /> : null}
      {tab === 'service' ? <ServicePanel projectId={project} open={search.machine} /> : null}
      {tab === 'waybills' ? (
        project ? (
          <FleetWaybills projectId={project} />
        ) : (
          <EmptyState title="Выберите проект">Наряды и путевые листы ведутся по проекту.</EmptyState>
        )
      ) : null}
      {tab === 'timesheet' ? <TimesheetPanel projectId={project} /> : null}
      {tab === 'fuel' ? <FuelPanel projectId={project} /> : null}
      {tab === 'cost' ? <CostPanel projectId={project} /> : null}
      {tab === 'output' ? (
        project ? (
          <OutputPanel projectId={project} />
        ) : (
          <EmptyState title="Выберите проект">Выработка считается по работам графика проекта.</EmptyState>
        )
      ) : null}
    </Page>
  );
}
